import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, ASSET_URL } from "./api";

const SiteCtx = createContext({});
export const useSite = () => useContext(SiteCtx);

const applyTheme = (theme) => {
  if (!theme) return;
  const r = document.documentElement.style;
  r.setProperty("--accent", theme.accent_color || "#C5A059");
  r.setProperty("--accent-contrast", theme.accent_contrast || "#FFFFFF");
  r.setProperty("--bg", theme.bg_color || "#FFFFFF");
  r.setProperty("--ink", theme.text_color || "#0A0A0A");
  r.setProperty("--muted", theme.text_muted || "#5C5C5C");
  r.setProperty("--line-soft", theme.border_color || "#E5E5E5");
  if (theme.radius != null) r.setProperty("--radius", `${theme.radius}px`);
};

export function SiteProvider({ children }) {
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState([]);
  const [lang, setLangState] = useState(() => new URLSearchParams(window.location.search).get("lang") === "en" ? "en" : "de");

  const refresh = useCallback(async () => {
    const [s, p] = await Promise.all([api.settings(), api.pages()]);
    setSettings(s);
    setPages(p);
    applyTheme(s?.theme);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    localStorage.setItem("drb_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // The URL is the single source of truth: ?lang=en selects English, while a
  // plain URL selects German. This also makes the DE button reliably remove
  // the English parameter instead of a stale state adding it again.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlLang = params.get("lang") === "en" ? "en" : "de";
    if (lang !== urlLang) setLangState(urlLang);
  }, [location.search, lang]);

  const setLang = useCallback((next) => {
    const value = typeof next === "function" ? next(lang) : next;
    const normalized = value === "en" ? "en" : "de";
    const params = new URLSearchParams(location.search);
    if (normalized === "en") params.set("lang", "en");
    else params.delete("lang");
    const search = params.toString();
    window.location.assign(`${location.pathname}${search ? `?${search}` : ""}${location.hash || ""}`);
  }, [lang, location.pathname, location.search, location.hash]);

  const t = useCallback((de, en) => (lang === "en" ? (en || de) : de), [lang]);

  return (
    <SiteCtx.Provider value={{ settings, pages, lang, setLang, t, refresh, applyTheme, ASSET_URL }}>
      {children}
    </SiteCtx.Provider>
  );
}
